import React, { useRef, useEffect, useCallback } from 'react';

import Validate from 'validate.js';

import { KEYS } from '../../../../../../../../constants/Common';

import AuthService from '../../../../../../../../services/Api/Auth';

import connectAuth, { IAuthMapStateToProps, IConnectedProps } from '../../../../hooks/connectAuth';


import Input from '../../../../../Common/Form/Input/Input';
import InputTrailingIcon from '../../../../../Common/Form/Input/InputTrailingIcon';
import { IAuthState } from '../../../../Store/Reducers';
import { Types, initialState as reducerInitialState } from '../../../../Store/SignUpReducer';

interface IInputPassword {
    error: string,
    reset: boolean,
    touched: boolean,
    password: string,
    visiblePassword: boolean,
    customPlaceholder?: string,
}

interface InputPasswordAttributes {
    isLogin?: boolean
}

type TInputPassword = IConnectedProps<IInputPassword> & InputPasswordAttributes;

const initialState = reducerInitialState.SignUp.personalData.password;

const FIELD_ID = 'input-password';

const InputPassword: React.FC<TInputPassword> = ({ intl, dispatch, isLogin = false, ...props }) => {
    const input = useRef<HTMLInputElement>(null);
    const $AuthService = new AuthService();

    const {
        error,
        reset,
        touched,
        password,
        visiblePassword,
        customPlaceholder
    } = props;

    const placeholder = customPlaceholder || intl.formatMessage({ id: 'authFunctional.personalData.placeholders.password' });

    const handleClickVisibility = () => {
        dispatch({
            type: Types.CHANGE_PERSONAL_FIELD_DATA,
            field: 'password',
            data: { visiblePassword: !visiblePassword }
        });

        if (!input.current) return;

        input.current.focus();
    };

    const handleChangePassword = ({ target }: React.ChangeEvent<HTMLInputElement>) => dispatch({
        type: Types.CHANGE_PERSONAL_FIELD_DATA,
        field: 'password',
        data: { error: '', value: target.value }
    });

    const handleBlurPassword = () => {
        if (!touched) return;

        dispatch({
            type: Types.CHANGE_PERSONAL_FIELD_DATA,
            field: 'password',
            data: (() => {
                if (isLogin) return { value: password };

                const passwordRule = {
                    presence: {
                        message: intl.formatMessage({ id: 'authFunctional.errors.passwordEmpty' })
                    },

                    length: {
                        minimum: 8,
                        tooShort: intl.formatMessage({ id: `authFunctional.errors.password.${isLogin ? 'invalidPassword' : 'tooShort'}` })
                    }
                };

                const isValid = Validate.single(password, passwordRule);

                if (isValid) return { error: isValid[0] };

                const errorId = $AuthService.validatePassword(password);
                if (errorId) return { error: intl.formatMessage({ id: errorId }) };

                return { value: password };
            })()
        });
    };

    const handleOnKeyDown = useCallback(({ key }: React.KeyboardEvent<HTMLInputElement>) => {
        if (error || !password || key !== KEYS.ENTER) return;

        const loginButton = document.getElementById('auth-login-button');

        if (!loginButton) return;

        loginButton.click();
    }, [error, password]);

    useEffect(() => {
        if (!reset) return;

        dispatch({
            type: Types.CHANGE_PERSONAL_FIELD_DATA,
            field: 'password',
            data: { initialState }
        });
    }, [reset, dispatch]);

    return (
        <Input
            order={8}
            ref={input}
            error={error}
            id={FIELD_ID}
            value={password}
            placeholder={placeholder}
            onBlur={handleBlurPassword}
            onKeyDown={handleOnKeyDown}
            data-cy="input-auth-password"
            onChange={handleChangePassword}
            type={visiblePassword ? 'text' : 'password'}
        >
            <InputTrailingIcon
                width={16}
                height={16}
                tabIndex={0}
                onClick={handleClickVisibility}
                data-cy="input-auth-password-icon"
            >
                { visiblePassword
                    ? (<path d="M8 2.547A8.57 8.57 0 0 0 0 8a8.57 8.57 0 0 0 8 5.453A8.57 8.57 0 0 0 16 8a8.57 8.57 0 0 0-8-5.453zm0 9.09A3.603 3.603 0 0 1 4.363 8 3.603 3.603 0 0 1 8 4.363 3.603 3.603 0 0 1 11.637 8 3.603 3.603 0 0 1 8 11.637zm0-5.82c-1.238 0-2.184.945-2.184 2.183S6.762 10.184 8 10.184c1.238 0 2.184-.946 2.184-2.184 0-1.238-.946-2.184-2.184-2.184zm0 0" />)
                    : (<path d="M8.035 3.98a3.62 3.62 0 0 1 3.656 3.653c0 .441-.074.953-.293 1.316l2.118 2.121c1.097-.953 1.972-2.12 2.484-3.437a8.61 8.61 0 0 0-8.035-5.477c-1.024 0-1.973.219-2.922.512l1.605 1.605c.438-.218.95-.293 1.387-.293zM.73 2.008L2.41 3.69l.293.29C1.461 4.93.512 6.17 0 7.632a8.607 8.607 0 0 0 8.035 5.48c1.098 0 2.192-.218 3.215-.582l.293.29 2.117 2.12.953-.949L1.68 1.06zm4.02 4.02l1.094 1.097v.508c0 1.242.949 2.195 2.191 2.195.149 0 .293 0 .512-.074l1.098 1.094a3.129 3.129 0 0 1-1.61.441 3.62 3.62 0 0 1-3.652-3.656c0-.582.148-1.094.367-1.606zm3.14-.587l2.266 2.266v-.144c0-1.243-.949-2.192-2.191-2.192 0 .07 0 .07-.074.07zm0 0" />)
                }
            </InputTrailingIcon>
        </Input>
    );
};

const mapStateToProps: IAuthMapStateToProps<IInputPassword> = (state, isComparison = false) => ((!isComparison)
    ? {
        reset: (state as IAuthState).Container.reset,
        error: (state as IAuthState).SignUp.personalData.password.error,
        password: (state as IAuthState).SignUp.personalData.password.value,
        touched: (state as IAuthState).SignUp.personalData.password.touched,
        visiblePassword: (state as IAuthState).SignUp.personalData.password.visiblePassword,
        customPlaceholder: (state as IAuthState).Container.communication.placeholderPassword
    } :
    {
        reset: state.reset,
        error: state.error,
        touched: state.touched,
        password: state.password,
        visiblePassword: state.visiblePassword,
        customPlaceholder: state.customPlaceholder
    }
);

export default connectAuth<IInputPassword, InputPasswordAttributes>(mapStateToProps)(InputPassword);
